<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;

    import DropArea from './DropArea.svelte';
    import { __, generateElementId, getActualKey, ResizeObserver } from '@oat-sa-private/ui-core';
    import { createEventDispatcher, onMount, onDestroy } from 'svelte';

    const dispatch = createEventDispatcher();
    const modes = Object.freeze({
        grid: 'grid',
        flex: 'flex'
    });

    export let draggableGroupKey; //for DropArea; key (unique identifier) shared between Draggables/DropAreas that are allowed to be dragged/dropped to each other
    export let dropAreaKey = generateElementId('dropArea'); //key (unique identifier) of DropArea
    export let ariaLabel = __('draggable list'); //aria-label for list; text about count of items in list will be appended to it
    export let ariaDescribedBy; //aria-describedby for list
    export let disabled; //disable component
    export let presentItemsCount = 0; //present items are list items that can be dragged and focused
    export let presentItemSelector = 'li:not(.removed) .draggable-container > .item-btn'; //query selector for focusable element inside list item, for arrow key behavior
    export let itemSelector = 'li .draggable-container > .item-btn'; //query selector for element inside list item, itemHeight of which should be measured
    export let inListNavigation = true; // enables arrow key navigation between list items
    export let tabindex; // tabindex for list
    export let suggested = false; // shows suggested style
    export let targetable = false; // increase hitbox of DropArea on drag-start of element that can potentially be dropped here
    export let targeted = false; // show 'targetable' DropArea visually, with special style indicating element can be dropped on it
    export let isListVertical = true; //list orientation: horizontal, vertical
    export let tabbable = true; //false if focus is inside this list; use it on this list's items to make them behave as a single tabstop

    //two-way binding props:
    export let itemHeight; //calculated max height of item in this list; use it on items in another lists  to control their height
    export let itemWidth; //calculated max width of item in this list; use it on items in another lists to control their width

    export let mode = modes.grid; //which css approach use for rendering list: grid or flex
    export let gridSize = 1; // number of cols/rows in case of grid mode
    export let instructionsLang; // user language to read instructions

    let container;
    let listHeight;

    const listLabelId = generateElementId('list-label');

    $: style = getItemStyles(itemWidth, itemHeight);

    /**
     * Calculate style variables for item's dimensions in list
     * @returns {String} style variables
     */
    function getItemStyles() {
        let itemStyles = '';
        itemStyles += itemWidth ? `--item-width:${itemWidth}px;` : '';
        itemStyles += itemHeight ? `--item-height:${itemHeight}px;` : '';
        itemStyles += modes[mode] === modes.grid ? `--grid-size:${gridSize}` : '';
        return itemStyles;
    }

    /**
     * Handle 'update' on DropArea
     * @param {CustomEvent} event
     * @fires 'drop' if item is dropped to this area
     */
    function handleDropAreaUpdate(event) {
        if (event.detail.draggableKey !== null) {
            dispatch('drop', event.detail);
        }
    }

    /**
     * Handle 'dragOver' on DropArea
     * @param {CustomEvent} event
     * @fires 'dragOver' if item from another area is dragged over this area
     */
    function handleDropAreaOver(event) {
        if (event.detail.dropAreaKey !== event.detail.initialDropAreaKey) {
            dispatch('dragOver', event.detail);
        }
    }

    /**
     * Handle keypress on container to navigate through items
     * @param {KeyboardEvent} event
     */
    function handleKeyDown(event) {
        const key = getActualKey(event);
        switch (key) {
            case 'down':
            case 'right': {
                if (inListNavigation) {
                    const allElements = findItemElements();
                    const focusedIndex = allElements.indexOf(document.activeElement);
                    if (focusedIndex >= 0) {
                        const nextIndex = focusedIndex === allElements.length - 1 ? 0 : focusedIndex + 1;
                        allElements[nextIndex].focus();
                    }
                    event.preventDefault();
                }
                break;
            }
            case 'up':
            case 'left': {
                if (inListNavigation) {
                    const allElements = findItemElements();
                    const focusedIndex = allElements.indexOf(document.activeElement);
                    if (focusedIndex >= 0) {
                        const nextIndex = focusedIndex === 0 ? allElements.length - 1 : focusedIndex - 1;
                        allElements[nextIndex].focus();
                    }
                    event.preventDefault();
                }
                break;
            }
        }
    }

    /**
     * Handle focusin on container:
     * to achieve single-tabstop behavior, items inside container should be made not tabbable
     */
    function handleFocusin() {
        tabbable = false;
    }

    /**
     * Handle focusout on container:
     * restore tabbable in case window:focusin didn't fire (if someone used e.stopPropagation)
     */
    function handleFocusout() {
        setTimeout(() => {
            if (
                !tabbable &&
                container &&
                container !== document.activeElement &&
                !container.contains(document.activeElement)
            ) {
                tabbable = true;
            }
        }, 200);
    }

    /**
     * Handle focusin on window:
     * restore tabbable in case container:focusout didn't fire (Firefox & Safari do not fire it when focused element is removed)
     * @param {Event} e
     */
    function handleWindowFocusin(e) {
        if (!tabbable && container && container !== e.target && !container.contains(e.target)) {
            tabbable = true;
        }
    }

    /**
     * Get array of html elements for present (draggable and focusable) items
     * @returns {DOMNode[]} elements
     */
    function findItemElements() {
        return Array.from(container.querySelectorAll(presentItemSelector));
    }

    /**
     * Set up observer for dynamically exposing list item dimensions
     */
    const itemResizeObserver = new ResizeObserver(([entry]) => {
        window.requestAnimationFrame(() => {
            if (!targetable) {
                const { height, width } = entry.target.getBoundingClientRect();
                if (height && width) {
                    itemHeight = height;
                    itemWidth = width;
                }
            }
        });
    });

    /**
     * Set up observer for dynamically exposing container height
     */
    const listResizeObserver = new ResizeObserver(([entry]) => {
        window.requestAnimationFrame(() => {
            if (!targetable) {
                const { height: currentHeight } = entry.target.getBoundingClientRect();
                if (currentHeight) {
                    listHeight = currentHeight;
                }
            }
        });
    });

    onMount(() => {
        const firstItem = container.querySelector(itemSelector);
        if (firstItem) {
            itemResizeObserver.observe(firstItem);
        }
        if (container) {
            listResizeObserver.observe(container);
        }
    });

    onDestroy(() => {
        itemResizeObserver.disconnect();
        listResizeObserver.disconnect();
    });
</script>

<style>
    .height-keeper {
        --list-height: auto;
        --item-height: 6rem;
        --grid-gap: 2rem;
        --stacking-height: 0.5rem; /* see DraggableListButton: add small bottom margin so if last item in list has .stacking, it won't overlap anything */
        &.vertical {
            & .draggable-list {
                display: grid;
                grid-template-columns: auto;
                grid-auto-rows: 1fr;
                grid-gap: var(--grid-gap);
            }
        }
        &.horizontal {
            &:not(.grid) .draggable-list {
                display: flex;
                flex-wrap: wrap;
                & :global(li) {
                    margin-right: var(--grid-gap);
                    margin-bottom: var(--grid-gap);
                }
            }
            &.grid {
                & .draggable-list {
                    display: grid;
                    grid-template-columns: repeat(var(--grid-size), 1fr);
                    grid-auto-rows: 1fr;
                    grid-gap: var(--grid-gap);
                }
            }
        }
    }
    .draggable-list {
        list-style-type: none;
        padding: 0;
        margin: 0 0 var(--stacking-height) 0;
        /* maybe this can fix the bug where mirror of choice with image rarely has lesser height than needed */
        & :global(.draggable-mirror) {
            min-height: var(--item-height);
        }
    }

    .targetable.height-keeper {
        position: relative;
        min-height: calc(var(--list-height) + var(--stacking-height));
    }
    .targetable .draggable-list {
        z-index: var(--layer-2);
        position: absolute;
        top: -1.5rem;
        left: -1.5rem;
        right: -1.5rem;
        padding: calc(1.5rem - var(--border-medium-plus));
        padding-bottom: calc(1.5rem - var(--border-medium-plus) + var(--item-height) + var(--grid-gap));
        border: var(--border-medium-plus) dashed transparent;
    }
    .targetable.targeted .draggable-list {
        border-color: var(--color-border-active);
        border-radius: var(--radius-large);
        background-color: var(--color-bg-selection);
    }
</style>

<svelte:window on:focusin={handleWindowFocusin} />
<div
    class="height-keeper"
    class:targetable
    class:targeted
    class:grid={mode === modes.grid}
    class:vertical={isListVertical}
    class:horizontal={!isListVertical}
    style={listHeight && targetable ? `--list-height:${listHeight}px` : void 0}>
    <DropArea
        {disabled}
        {suggested}
        focusable={false}
        {draggableGroupKey}
        key={dropAreaKey}
        on:update={handleDropAreaUpdate}
        on:dragOver={handleDropAreaOver}
        on:dragOut
        on:click>
        <!-- svelte-ignore a11y-no-noninteractive-tabindex svelte-ignore a11y-no-noninteractive-element-interactions -->
        <ul
            class="draggable-list"
            role="application"
            {tabindex}
            aria-labelledby={listLabelId}
            aria-describedby={ariaDescribedBy ? ariaDescribedBy : void 0}
            style={style ? style : void 0}
            bind:this={container}
            on:keydown={handleKeyDown}
            on:keyup
            on:focusin={handleFocusin}
            on:focusout={handleFocusout}>
            <slot />
        </ul>
    </DropArea>
    <span
        id={listLabelId}
        class="hidden"
        lang={instructionsLang}
    >
        {__('%s, %d items', ariaLabel, presentItemsCount)}
    </span>
</div>
